import { createFileRoute } from "@tanstack/react-router";

import { MainLayout } from "@/components/layouts/main-layout";
import { usePendingSplitStore } from "@/components/shared/dropzones/document-split-dropzone";
import { ErrorCard } from "@/components/shared/errors/error-card";
import SplitPdfLoading from "@/components/shared/pdf-tools/components/split-pdf-loading";
import { EditPdfContainer } from "@/components/shared/pdf-tools/edit-pdf";
import { useUploadDocument } from "@/hooks/useUploadPdf";
import { useTenant } from "@/providers/session";
import * as Sentry from "@sentry/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute(
  "/_authenticated/$company/buchhaltung/_focused/rechnung/seiten-bearbeiten",
)({
  component: PdfSplit,
});

function PdfSplit() {
  const { t } = useTranslation();
  const { tenantId } = useTenant();

  const [pdfs, setPdfs] = useState<
    {
      invoiceId: string;
      fileName: string;
      pdfUrl: string;
      thumbnailUrls?: string[];
    }[]
  >([]);

  const { uploadDocument } = useUploadDocument({ tenantId });
  const { pdfFiles, clearFiles } = usePendingSplitStore();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const blobUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    if (!pdfFiles.length) return;

    const objectUrls = pdfFiles.map((file) => URL.createObjectURL(file));
    blobUrlsRef.current = objectUrls;

    const newPdfs = pdfFiles.map((file, index) => ({
      invoiceId: crypto.randomUUID(),
      fileName: file.name,
      pdfUrl: objectUrls[index],
    }));

    setPdfs(newPdfs);
    setErrorMessage(null);
  }, [pdfFiles]);

  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const handleNavigation = () => {
    window.history.back();
  };

  const handleSubmitLayout = async (
    layout: {
      invoiceId: string;
      pageIndex: number;
    }[][],
    pdfUrls: string[],
  ) => {
    const uploadPromises = pdfUrls.map(async (pdfUrl, index) => {
      try {
        const response = await fetch(pdfUrl);

        if (!response.ok) {
          throw new Error(
            `Failed to fetch PDF: ${response.status} ${response.statusText}`,
          );
        }

        const blob = await response.blob();

        const file = new File([blob], `sortiertes-dokument-${index + 1}.pdf`, {
          type: blob.type || "application/pdf",
        });

        const fileList = {
          0: file,
          length: 1,
          item: (index: number) => (index === 0 ? file : null),
          [Symbol.iterator]: function* () {
            yield file;
          },
        } as FileList;

        return new Promise<void>((resolve, reject) => {
          uploadDocument({
            acceptedFiles: fileList,
            onUploadError: (error: any) => {
              console.error("Upload failed:", error);
              reject(error);
            },
            onUploadSuccess: (result: any) => {
              console.log("Upload successful:", result);
              resolve();
            },
          });
        });
      } catch (error) {
        console.error(`Failed to process PDF ${index + 1}:`, error);
        Sentry.captureException(error);
        throw error;
      }
    });

    try {
      await Promise.all(uploadPromises);
      clearFiles();
      handleNavigation();
    } catch (error) {
      setErrorMessage(t("upload_error"));
      throw error;
    }
  };

  useEffect(() => {
    return () => {
      clearFiles();
    };
  }, [clearFiles]);

  if (errorMessage) {
    return (
      <div className="flex justify-center items-center flex-1">
        <ErrorCard errorMessage={errorMessage} title="Achtung" />
      </div>
    );
  }

  console.log("pdfs", pdfs);

  if (pdfs.length === 0) {
    return (
      <SplitPdfLoading
        onCancel={() => {
          window.history.back();
        }}
      />
    );
  }

  return (
    <EditPdfContainer
      initialPdfBlobs={pdfs}
      onSubmit={handleSubmitLayout}
      isProcessing={false}
    />
  );
}
